/**
 * Apple-Style API Reference Page with Floating Section Labels
 * No traditional sidebar - uses glassmorphic floating section indicators
 */

import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { apiReferencePageFooter } from './layout/footerConfigs'
import {
  IntroductionSection,
  AuthenticationSection,
  BaseUrlsSection,
  RateLimitsSection,
  JobsApiSection,
  ApplicationsApiSection,
  BrandSettingsApiSection,
  WebhooksSection,
  ErrorsSection
} from './sections/ApiReferenceSections'

export const ApiReferencePage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Scroll hook available if needed for future enhancements
  useOptimizedScroll()

  // Define sections for Apple-style floating labels
  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      children: <IntroductionSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'authentication',
      title: 'Authentication',
      children: <AuthenticationSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'base-urls',
      title: 'Base URLs',
      children: <BaseUrlsSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'rate-limits',
      title: 'Rate Limits',
      children: <RateLimitsSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'jobs-api',
      title: 'Jobs API',
      children: <JobsApiSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'applications-api',
      title: 'Applications API',
      children: <ApplicationsApiSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'brand-settings-api',
      title: 'Brand Settings API',
      children: <BrandSettingsApiSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'webhooks',
      title: 'Webhooks',
      children: <WebhooksSection prefersReducedMotion={prefersReducedMotion.current} />
    },
    {
      id: 'errors',
      title: 'Error Handling',
      children: <ErrorsSection prefersReducedMotion={prefersReducedMotion.current} />
    }
  ]

  return (
    <>
      {/* Navbar is now global - no individual page navbars needed */}

      {/* Main Content Wrapper with Apple-Style Design */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }} transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.3, 1], x: [0, -50, 0], y: [0, -30, 0] }} transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', willChange: prefersReducedMotion.current ? 'auto' : 'transform' }} animate={prefersReducedMotion.current ? {} : { scale: [1, 1.15, 1], x: [0, 30, 0], y: [0, -20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />

        {/* Apple-Style Content */}
        <div className="relative mx-auto pb-16" style={{
          maxWidth: '1200px',
          marginTop: '-20vh', // Reduced from -35vh for better spacing
          paddingLeft: '4rem',
          paddingRight: '4rem'
        }}>
          {/* Temporarily commented out - Sticky Section Labels with Scroll-Activation */}
          {/* <SectionObserver sections={sections} /> */}

          {/* Manual section rendering for now */}
          {sections.map((section, index) => (
            <div
              key={section.id}
              data-section-id={section.id}
              className="section-content"
              style={{
                marginBottom: index === 0 ? '4rem' : '6rem',
                minHeight: index === 0 ? '60vh' : '50vh',
                scrollMarginTop: '120px'
              }}
            >
              {section.children}
            </div>
          ))}
        </div>
      </div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.2, margin: "0px 0px -100px 0px" })

  return (
    <section ref={ref} className="relative flex items-center justify-center" style={{ backgroundColor: 'transparent', minHeight: '100vh' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.03em'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          API Reference
        </motion.h1>
        <motion.p
          className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8"
          style={{
            color: '#666666',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 500
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Everything you need to integrate Mernaki with your internal systems, automate job postings, manage applications, and use Mernaki's tools programmatically.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex items-center justify-center gap-4 flex-wrap"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <Link
            to="/contact"
            className="px-8 py-4 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none',
              fontSize: '15px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2A2A2A'
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.25)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#1A1A1A'
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.15)'
            }}
          >
            Get API Key
          </Link>
          <a
            href="#"
            className="relative px-8 py-4 rounded-xl transition-all duration-300 overflow-hidden group"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#1A1A1A',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none',
              fontSize: '15px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)'
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.12)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08)'
            }}
          >
            <span className="relative z-10">View Documentation</span>
          </a>
        </motion.div>

        {/* JobMeet API Link */}
        <motion.div
          className="mt-12 flex items-center justify-center"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <Link
            to="/api/jobmeet"
            className="px-8 py-4 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#1A1A1A',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none',
              fontSize: '15px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(10px)',
              display: 'inline-block'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 1)'
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.12)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.08)'
            }}
          >
            JobMeet Scheduler API
          </Link>
        </motion.div>
      </div>
    </section>
  )
}



const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  if (isMobile) {
    return <PageMobileFooter {...apiReferencePageFooter} />
  }

  return (
    <footer
      className="relative"
      style={{
        backgroundColor: '#0E0E0E',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        paddingTop: '100px', // Smaller padding for API Reference
        paddingBottom: '40px',
        marginTop: '-60px' // Smaller pull-up for API Reference
      }}
    >
      {/* Floating Gradient Divider */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Columns */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Column 1 - Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                alt="Mernaki Logo"
                className="object-contain"
                style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                loading="lazy"
              />
              <h3
                className="text-lg"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                  fontWeight: 400
                }}
              >
                Mernaki
              </h3>
            </div>
            <p
              className="text-sm leading-relaxed"
              style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400
              }}
            >
              Developer Platform for premium job pages and recruitment automation.
            </p>
          </div>

          {/* Column 2 - API Reference */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              API Reference
            </h3>
            <ul className="space-y-2">
              {[
                { label: 'Introduction', to: '#introduction' },
                { label: 'Authentication', to: '#authentication' },
                { label: 'Jobs API', to: '#jobs-api' },
                { label: 'Webhooks', to: '#webhooks' },
                { label: 'Changelog', to: '#' },
                { label: 'Status Page', to: '/status' }
              ].map((item, i) => (
                <li key={i}>
                  {item.to.startsWith('#') ? (
                    <a
                      href={item.to}
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.to}
                      className="text-sm transition-all duration-300"
                      style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 400,
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Company */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Company
            </h3>
            <ul className="space-y-2">
              {[
                { label: 'About', to: '/about' },
                { label: 'Privacy Policy', to: '/privacy-policy' },
                { label: 'Terms of Service', to: '/terms' },
                { label: 'Impressum', to: '/impressum' }
              ].map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Support & Developer Contact */}
          <div>
            <h3
              className="text-sm mb-4"
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Support
            </h3>
            <ul className="space-y-2 mb-6">
              {[
                { label: 'Help Center', to: '/help' },
                { label: 'Report an Issue', to: '/report-issue' },
                { label: 'Contact Us', to: '/contact' }
              ].map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.to}
                    className="text-sm transition-all duration-300"
                    style={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="space-y-2">
              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', sans-serif" }}>
                Developer Support:
              </p>
              <a
                href="mailto:developer@mernaki.de"
                className="text-sm transition-all duration-300"
                style={{
                  color: '#4A90E2',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 500,
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#357ABD'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4A90E2'}
              >
                developer@mernaki.de
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="pt-6 text-center" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <p
            className="text-sm"
            style={{
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 300
            }}
          >
            © {new Date().getFullYear()} Mernaki. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}